import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '../cn';

/**
 * A native `<dialog>` opened with `showModal()` is this area's modal. The focus
 * trap, the backdrop, `Esc`, and focus return to the trigger are the browser's
 * — none of them is hand-written, and none of them is re-implemented.
 *
 * The backdrop is the browser's own, so it is never a child of a `transform` /
 * `backdrop-filter` / `will-change` ancestor: each of those re-scopes
 * `position: fixed` and the containing-block trap follows.
 */
export function Dialog({
  open,
  onClose,
  labelledBy,
  children,
  className,
}: {
  open: boolean;
  onClose: (returnValue: string) => void;
  labelledBy: string;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleClose = () => onClose(element.returnValue);
    element.addEventListener('close', handleClose);
    return () => element.removeEventListener('close', handleClose);
  }, [onClose]);

  return (
    /*
     * `jsx-a11y` reads `<dialog>` as a plain non-interactive element and asks
     * for a keyboard listener beside the pointer one. There is nothing to add:
     * `showModal()` binds `Esc` to the same dismissal in the browser itself, so
     * the keyboard path already exists and hand-writing a second one would fire
     * the close twice. The rule cannot see that, so it is silenced here and
     * nowhere else.
     */
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      aria-labelledby={labelledBy}
      className={cn(
        'max-w-measure rounded-lg border border-border bg-surface-overlay p-6 text-ink shadow-4',
        'backdrop:bg-bg-sunken/70',
        className,
      )}
      onClick={(event) => {
        // A click on the element itself is a click on the backdrop: the panel
        // fills the element's padding box and swallows its own clicks.
        if (event.target === ref.current) ref.current?.close('dismiss');
      }}
    >
      {children}
    </dialog>
  );
}
